import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, Subject } from 'rxjs';

export interface Tag {
  name: string;
  color: string;
}

export interface InternalNote {
  text: string;
  createdAt: string;
  latest?: boolean;
}

export interface TourMeta {
  tourId: string;
  tags: Tag[];
  internalNotes: InternalNote[];
}

@Injectable({ providedIn: 'root' })
export class TourMetaService {

  // 🔥 GLOBAL EVENT STREAM
  private metaUpdatedSource = new Subject<{
    tourId: string;
    tags?: Tag[];
    internalNotes?: InternalNote[];
  }>();

  metaUpdated$ = this.metaUpdatedSource.asObservable();

  constructor(private http: HttpClient) { }

  // 🔥 Emit update
  notifyMetaUpdated(data: {
    tourId: string;
    tags?: Tag[];
    internalNotes?: InternalNote[];
  }) {
    this.metaUpdatedSource.next(data);
  }

  // Get full tour meta (tags + notes)
  getTourMeta(tourId: string): Observable<TourMeta> {
    return this.http.get<TourMeta>(`${environment.baseUrl}tours/${tourId}/meta`);
  }

  // Tags
  addTag(tourId: string, tag: Tag, propertyId?: string): Observable<TourMeta> {
    return this.http.post<TourMeta>(`${environment.baseUrl}tours/${tourId}/tag`, { tag, propertyId });
  }

  getGlobalTags(propertyId: string, search: string = ''): Observable<{ name: string; color: string }[]> {
    return this.http.get<{ name: string; color: string }[]>(
      `${environment.baseUrl}tours/global-tags?propertyId=${propertyId}&search=${encodeURIComponent(search)}`
    );
  }

  removeTag(tourId: string, tagName: string): Observable<TourMeta> {
    return this.http.delete<TourMeta>(`${environment.baseUrl}tours/${tourId}/tag/${tagName}`);
  }

  // Notes
  addNote(tourId: string, note: string): Observable<TourMeta> {
    return this.http.post<TourMeta>(`${environment.baseUrl}tours/${tourId}/note`, { note });
  }

  removeNote(tourId: string, createdAt: string | Date): Observable<TourMeta> {
    const encodedDate = encodeURIComponent(createdAt.toString());
    return this.http.delete<TourMeta>(`${environment.baseUrl}tours/${tourId}/note/${encodedDate}`);
  }

  setLatestNote(tourId: string, noteCreatedAt: string | Date): Observable<TourMeta> {
    return this.http.put<TourMeta>(`${environment.baseUrl}tours/${tourId}/note/latest`, { createdAt: noteCreatedAt });
  }
}